import React, { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'react-query';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import Checkbox from 'material-ui/Checkbox';
import IconButton from 'material-ui/IconButton';
import NavigationClose from 'material-ui/svg-icons/navigation/close';
import SwapHoriz from 'material-ui/svg-icons/action/swap-horiz';
import LinearProgress from 'material-ui/LinearProgress';
import { compareSuites, listSuites } from 'services/testRuns';
import { formatSuiteLabel, runSelectionTip } from './metrics';
import CompareResults from './CompareResults';
import { HoverTip } from './HoverTip';
import './styles.scss';

function ClearCompare({ onClick }) {
    return (
        <HoverTip label="Remove">
            <IconButton onClick={onClick}>
                <NavigationClose />
            </IconButton>
        </HoverTip>
    );
}

export default function ComparePage({ location, router }) {
    const from = location.query.from;
    const to = location.query.to;
    const [percentMode, setPercentMode] = useState(true);
    const suitesQuery = useQuery('test-runs', listSuites);
    const { data, error, isLoading } = useQuery(
        ['test-run-compare', from, to],
        () => compareSuites(from, to),
        { enabled: Boolean(from && to) },
    );

    const suites = suitesQuery.data || [];
    const completed = suites.filter((row) => row.status === 'completed');
    const fromRow = suites.find(row => row.id === from) || (data && data.from);
    const toRow = suites.find(row => row.id === to) || (data && data.to);

    const setPair = (nextFrom, nextTo) => {
        const params = [];
        if (nextFrom) {
            params.push(`from=${encodeURIComponent(nextFrom)}`);
        }
        if (nextTo) {
            params.push(`to=${encodeURIComponent(nextTo)}`);
        }
        router.push(params.length ? `/bbr/runs/compare?${params.join('&')}` : '/bbr/runs/compare');
    };

    const swap = () => {
        if (!from || !to) {
            return;
        }
        setPair(to, from);
    };

    const removeRun = (id) => {
        const kept = id === from ? to : from;
        if (kept) {
            router.push(`/bbr/runs/${kept}`);
            return;
        }
        setPair(id === from ? null : from, id === to ? null : to);
    };

    return (
        <div className="test-runs-page">
            <div className="test-runs-toolbar">
                <h2>
                    <Link to="/bbr/runs">Recognition tests</Link>
                    {' / compare'}
                </h2>
                <div className="test-runs-toolbar-actions">
                    <div className="test-runs-compare-base">
                        <HoverTip label={runSelectionTip(fromRow)}>
                            <SelectField
                                floatingLabelText="From (baseline)"
                                value={from || null}
                                onChange={(e, i, value) => setPair(value, to)}
                                style={{ width: 280 }}
                            >
                                {completed.map(row => (
                                    <MenuItem key={row.id} value={row.id} primaryText={formatSuiteLabel(row)} />
                                ))}
                            </SelectField>
                        </HoverTip>
                        {from ? (
                            <ClearCompare onClick={() => removeRun(from)} />
                        ) : null}
                    </div>
                    <HoverTip label="Swap order">
                        <IconButton onClick={swap} disabled={!from || !to}>
                            <SwapHoriz />
                        </IconButton>
                    </HoverTip>
                    <div className="test-runs-compare-base">
                        <HoverTip label={runSelectionTip(toRow)}>
                            <SelectField
                                floatingLabelText="To (candidate)"
                                value={to || null}
                                onChange={(e, i, value) => setPair(from, value)}
                                style={{ width: 280 }}
                            >
                                {completed.map(row => (
                                    <MenuItem key={row.id} value={row.id} primaryText={formatSuiteLabel(row)} />
                                ))}
                            </SelectField>
                        </HoverTip>
                        {to ? (
                            <ClearCompare onClick={() => removeRun(to)} />
                        ) : null}
                    </div>
                    {from && to ? (
                        <Checkbox
                            label="Percent"
                            checked={percentMode}
                            onCheck={(e, checked) => setPercentMode(checked)}
                            style={{ width: 'auto', marginBottom: 8 }}
                        />
                    ) : null}
                </div>
            </div>
            {!from || !to ? (
                <p>Select two completed runs. Default from the list is earlier → later; swap inverts.</p>
            ) : error ? (
                <div className="test-runs-error">{error.message}</div>
            ) : isLoading || !data ? (
                <LinearProgress />
            ) : (
                <CompareResults
                    data={data}
                    percentMode={percentMode}
                    onOpenDoc={(docId) => {
                        router.push(`/bbr/runs/${to}/docs/${docId}?compare=${encodeURIComponent(from)}`);
                    }}
                />
            )}
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/ComparePage.jsx